import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';

interface ErrorContextType {
    error: string | null;
    title: string | null;
    showError: (error: string, title?: string) => void;
    clearError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const ErrorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [error, setErrorState] = useState<string | null>(null);
    const [title, setTitleState] = useState<string | null>(null);

    const showError = useCallback((errorMessage: string, errorTitle: string = 'Error') => {
        setErrorState(errorMessage);
        setTitleState(errorTitle);
    }, []);

    const clearError = useCallback(() => {
        setErrorState(null);
        setTitleState(null);
    }, []);

    // Listen for API permission errors from the API client
    useEffect(() => {
        const handlePermissionError = (event: CustomEvent) => {
            const { message, title: errorTitle } = event.detail;
            showError(message, errorTitle || 'Permission Denied');
        };

        window.addEventListener('api-permission-error', handlePermissionError as EventListener);
        return () => {
            window.removeEventListener('api-permission-error', handlePermissionError as EventListener);
        };
    }, [showError]);

    return (
        <ErrorContext.Provider value={{ error, title, showError, clearError }}>
            {children}
        </ErrorContext.Provider>
    );
};

export const useError = () => {
    const context = useContext(ErrorContext);
    if (context === undefined) {
        throw new Error('useError must be used within a ErrorProvider');
    }
    return context;
};
